"use client";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState, startTransition } from "react";
import SearchBar from "@/components/ui/SearchBar";
import CenteredListOrGrid from "@/components/ui/CenteredListOrGrid";
import Pagination from "@/components/ui/Pagination";
import Breadcrumbs from "@/components/ui/Breadcrumbs";

const PAGE_SIZE = 12;

function buildListUrl(pathname, searchParams) {
    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
}

export default function ListPage({
    Card,
    title = "",
    placeholder = "Search…",
    breadcrumbs = [],
    initialData = [],
    initialMeta = { page: 1, totalPages: 1, total: 0 },
    initialQ = "",
    cardPropName = "",
}) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // Input value (instant)
    const [q, setQ] = useState(initialQ);

    // Keep input in sync with URL (back/forward, external nav)
    useEffect(() => {
        const spQ = searchParams.get("q") || "";
        if (spQ !== q) setQ(spQ);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const getCardProps = (item) =>
        cardPropName ? { [cardPropName]: item } : item;

    const items = initialData;
    const meta = initialMeta;

    // Build and push URL only when debounced value changes
    const pushSearchToUrl = (value) => {
        const current = searchParams.get("q") || "";
        if (value === current) return; // no-op if unchanged

        const params = new URLSearchParams(searchParams.toString());
        if (value) params.set("q", value);
        else params.delete("q");
        params.delete("page"); // page 1 should resolve to the canonical list URL

        // Transition for smoother UX
        startTransition(() => {
            router.replace(buildListUrl(pathname, params));
        });
    };

    const handlePageChange = (newPage) => {
        const params = new URLSearchParams(searchParams.toString());
        if (newPage <= 1) params.delete("page");
        else params.set("page", String(newPage));
        startTransition(() => {
            router.replace(buildListUrl(pathname, params));
        });
    };

    return (
        <section className="px-4 sm:px-6 lg:px-8 py-12">
            <div className="max-w-5xl mx-auto px-4 py-12">
                {breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
                {title && (
                    <h1 className="text-3xl font-extrabold font-heading text-gray-900 tracking-tight mb-4 text-center">
                        {title}
                    </h1>
                )}

                <div className="flex flex-col items-center gap-3 mb-6">
                    <SearchBar
                        placeholder={placeholder}
                        value={q}
                        onChange={(e) => setQ(e.target.value)} // instant input
                        onDebouncedChange={pushSearchToUrl} // URL after pause
                        debounceMs={400} // tweak as you like (300–500ms)
                        onClear={() => pushSearchToUrl("")}
                        action={pathname}
                    />
                </div>

                {!items || items.length === 0 ? (
                    <div className="text-gray-500 text-center py-24">
                        {q
                            ? `No results found for “${q}”.`
                            : "No results found."}
                    </div>
                ) : (
                    <CenteredListOrGrid ariaLabel="List">
                        {items.map((item) => (
                            <Card
                                key={item.slug || item._id || item.id}
                                {...getCardProps(item)}
                            />
                        ))}
                    </CenteredListOrGrid>
                )}

                <Pagination
                    page={meta.page}
                    totalPages={meta.totalPages}
                    total={meta.total}
                    onPageChange={handlePageChange}
                />
            </div>
        </section>
    );
}
